import { connect } from 'react-redux';
import { useState, useEffect } from 'react';
import subscriptionHelpers from '../../helpers/subscriptionHelpers';

import { EssentialsProductMarkerProps, EssentialsProductMarkerStateProps } from '../../types/components/shared/EssentialsProductMarker';
import { ApplicationState } from '../../types/state/storeTypes';
import { SubscriptionDetail } from '../../types/api/SubscriptionTypes';
import { EssentialsLogo, ClockIcon } from '../../modules/Icons';

import EssentialsMonthlyExpiryDate from './EssentialsMonthlyExpiryDate';

export const defaultViewClassName = 'py-4 px-4 md:px-6 rounded-[5px] bg-[#24C1FF1F] w-full';
export const minimalViewClassName = 'py-4 px-6 rounded-[5px] bg-[#24C1FF1F] w-full text-center';

const EssentialsProductMarker: React.FC<EssentialsProductMarkerProps> = (props: EssentialsProductMarkerProps) => {
  const { subscription, minimalView, essentialsMonthlyExpiryDate } = props;
  const { isLegacy } = subscriptionHelpers;

  const [formattedEcExpiryDate, setFormattedEcExpiryDate] = useState<string>();

  useEffect(() => {
    if (!essentialsMonthlyExpiryDate) return;
    const formattedDate = new Date(essentialsMonthlyExpiryDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    setFormattedEcExpiryDate(formattedDate);
  }, [essentialsMonthlyExpiryDate]);

  if (subscription && isLegacy(subscription)) return <></>

  const renderEssentialsLogo = (className?: string): React.ReactElement => {
    return (
      <EssentialsLogo className={`inline-block align-top rounded-[5px] ${className}`} />
    );
  }

  const renderNoSubscriptionViewTitle = (): React.ReactElement => {
    return (
      <div className="flex flex-col gap-4">
        <div cy-test-id="no-subscription-view-title" className="h-[22px] whitespace-nowrap ">
          <span className="uppercase text-white eurostile text-[16px] md:text-[18px] font-semibold tracking-wide leading-[20px] align-super mr-[-10px] sm:mr-0">
            included in
          </span>
          {renderEssentialsLogo('scale-[80%] md:scale-[100%] md:ml-2')}
        </div>
        <EssentialsMonthlyExpiryDate expiryDate={essentialsMonthlyExpiryDate} />
      </div>
    );
  }

  const renderNoSubscriptionView = (): React.ReactElement => {
    if (!!subscription) return;
    if (minimalView)
      return renderNoSubscriptionViewTitle();
    return (
      <>
        {renderNoSubscriptionViewTitle()}
        <p className="mt-4 inter text-[12px] md:text-[14px] font-light md:leading-22 tracking-wide" cy-test-id="no-subscription-view-paragraph">
          This product is part of the Essentials Catalog and can be downloaded freely by all paying subscribers{formattedEcExpiryDate ? ` until ${formattedEcExpiryDate}` : ''}. <span className="font-bold">No credits required!</span>
        </p>
      </>
    )
  }

  const renderSubscriptionView = (): React.ReactElement => {
    if (!subscription) return;
    return (
      <div className="flex flex-col gap-4">
        <p className="inter text-[14px] font-light leading-22" cy-test-id="subscription-view-paragraph">
          As part of your subscription, you have access to
          <span className="ml-1 uppercase text-white eurostile text-16 font-bold leading-[20px] align-bottom">the {renderEssentialsLogo('mx-1')} catalog</span>.
          Download this product now{essentialsMonthlyExpiryDate ? ' before it\'s gone' : ''}!
        </p>
        <EssentialsMonthlyExpiryDate expiryDate={essentialsMonthlyExpiryDate} />
      </div>
    )
  }

  return (
    <div cy-test-id="essentials-product-marker" className={minimalView ? minimalViewClassName : defaultViewClassName}>
      {renderNoSubscriptionView()}
      {renderSubscriptionView()}
    </div>
  )
}

const mapStateToProps = (state: ApplicationState): EssentialsProductMarkerStateProps => ({
  subscription: state.auth.subscription as SubscriptionDetail,
});

export default connect(mapStateToProps)(EssentialsProductMarker);
